"use client"

import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useEffect, useState, type ReactNode } from "react"
import {
  Home,
  Ticket,
  Package,
  Store,
  MoreHorizontal,
  Activity,
  Swords,
  HeartPulse,
  Send,
  Users2,
  Smartphone,
  Settings,
  LogOut,
  X,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useSidebar } from "@/contexts/sidebar-context"
import { useTenant } from "@/contexts/tenant-context"
import { useAuth } from "@/contexts/auth-context"

interface DockItem {
  id: string
  label: string
  href: string
  icon: typeof Home
  requiredFeature?: string | null
}

const PRIMARY_ITEMS: DockItem[] = [
  { id: "overview", label: "Visão", href: "/dashboard-cms", icon: Home },
  { id: "coupons", label: "Cupons", href: "/dashboard-cms/cupons", icon: Ticket },
  { id: "products", label: "Produtos", href: "/dashboard-cms/produtos", icon: Package },
  { id: "stores", label: "Lojas", href: "/dashboard-cms/lojas", icon: Store },
]

const MORE_ITEMS: DockItem[] = [
  { id: "realtime", label: "Tempo real", href: "/dashboard-cms/realtime", icon: Activity, requiredFeature: "realtime" },
  { id: "spy", label: "Spy", href: "/dashboard-cms/spy", icon: Swords, requiredFeature: "spy" },
  { id: "health", label: "Saúde", href: "/dashboard-cms/saude", icon: HeartPulse, requiredFeature: "saude" },
  { id: "telegram", label: "Telegram", href: "/dashboard-cms/ferramentas/telegram", icon: Send },
  { id: "members", label: "Membros", href: "/dashboard-cms/equipe/membros", icon: Users2, requiredFeature: "membros" },
  { id: "app", label: "App mobile", href: "/dashboard-cms/app", icon: Smartphone },
  { id: "settings", label: "Configurações", href: "/settings", icon: Settings },
]

function isActiveHref(pathname: string | null, href: string): boolean {
  if (!pathname) return false
  if (href === "/dashboard-cms") return pathname === "/dashboard-cms"
  return pathname === href || pathname.startsWith(`${href}/`)
}

function DockButton({
  active,
  onClick,
  href,
  icon,
  label,
}: {
  active: boolean
  onClick?: () => void
  href?: string
  icon: ReactNode
  label: string
}) {
  const inner = (
    <span
      className={cn(
        "relative flex h-full w-full flex-col items-center justify-center gap-0.5 pt-1.5 transition-colors",
        active ? "text-[var(--brand-amber)]" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute -top-px left-1/2 h-[2px] w-7 -translate-x-1/2 rounded-full bg-[var(--brand-amber)]"
        />
      )}
      <span className="flex h-6 w-6 items-center justify-center">{icon}</span>
      <span className="font-mono text-[9.5px] uppercase tracking-[0.08em] leading-none">{label}</span>
    </span>
  )

  if (href) {
    return (
      <Link
        href={href}
        aria-label={label}
        aria-current={active ? "page" : undefined}
        className="flex h-full min-w-0 flex-1 items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-inset"
      >
        {inner}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex h-full min-w-0 flex-1 items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-inset"
    >
      {inner}
    </button>
  )
}

function MoreSheet({
  isOpen,
  onClose,
  items,
  pathname,
  tenantName,
  onLogout,
}: {
  isOpen: boolean
  onClose: () => void
  items: DockItem[]
  pathname: string | null
  tenantName: string
  onLogout: () => void
}) {
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [isOpen, onClose])

  return (
    <div
      className={cn(
        "fixed inset-0 z-[80] flex flex-col justify-end transition-opacity duration-200 lg:hidden",
        isOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
      )}
      role="dialog"
      aria-modal={isOpen ? "true" : undefined}
      aria-label="Mais opções"
      onClick={onClose}
    >
      <div
        aria-hidden="true"
        className={cn(
          "absolute inset-0 bg-foreground/40 backdrop-blur-sm transition-opacity duration-200",
          isOpen ? "opacity-100" : "opacity-0",
        )}
      />
      <section
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "relative max-h-[80vh] overflow-y-auto rounded-t-3xl border border-panel-border border-b-0 bg-panel shadow-float-strong transition-transform duration-300 ease-out",
          isOpen ? "translate-y-0" : "translate-y-full",
        )}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
      >
        <div className="flex flex-col items-center px-4 pb-2 pt-3">
          <span aria-hidden="true" className="h-1 w-10 rounded-full bg-border/60" />
        </div>

        <div className="flex items-baseline justify-between px-5 pb-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Mais opções
            </p>
            <h2 className="mt-0.5 font-display text-xl italic text-foreground">{tenantName}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <ul className="divide-y divide-border/40 px-2">
          {items.map((item) => {
            const Icon = item.icon
            const active = isActiveHref(pathname, item.href)
            return (
              <li key={item.id}>
                <Link
                  href={item.href}
                  onClick={onClose}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex min-h-12 items-center gap-3 rounded-lg px-3 py-3 text-sm transition-colors",
                    active
                      ? "bg-[var(--brand-amber)]/8 text-foreground"
                      : "text-foreground/85 hover:bg-muted/40",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-md transition-colors",
                      active ? "text-[var(--brand-amber)]" : "text-muted-foreground",
                    )}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="flex-1 font-medium">{item.label}</span>
                  {active && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--brand-amber)]">
                      aqui
                    </span>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>

        <div className="mt-2 border-t border-border/40 px-2 pt-2">
          <button
            type="button"
            onClick={() => {
              onClose()
              onLogout()
            }}
            className="flex min-h-12 w-full items-center gap-3 rounded-lg px-3 py-3 text-sm text-rose-600 transition-colors hover:bg-rose-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-md">
              <LogOut className="h-[18px] w-[18px]" />
            </span>
            <span className="flex-1 text-left font-medium">Sair</span>
          </button>
        </div>
      </section>
    </div>
  )
}

export default function MobileDock() {
  const pathname = usePathname()
  const router = useRouter()
  const { isMobile, isMobileOpen } = useSidebar()
  const { tenant, hasFeature } = useTenant()
  const { logout } = useAuth()
  const [moreOpen, setMoreOpen] = useState(false)

  // Close the more-sheet when route changes.
  useEffect(() => {
    setMoreOpen(false)
  }, [pathname])

  const visibleMore = MORE_ITEMS.filter((item) => !item.requiredFeature || hasFeature(item.requiredFeature))

  const moreActive = visibleMore.some((item) => isActiveHref(pathname, item.href))

  const hide = !isMobile || isMobileOpen || (pathname?.startsWith("/auth") ?? false)

  return (
    <>
      <nav
        aria-label="Atalhos rápidos"
        className={cn(
          "fixed inset-x-0 bottom-0 z-[55] lg:hidden",
          // Liquid glass iOS 26 com vibe da marca: bg translúcido (30%) +
          // blur 30px + saturate 200% + luminação âmbar sutil no topo.
          // A "luz" vem da marca, não chapada — combina inset highlight
          // âmbar com gradient overlay de cima pra baixo.
          "bg-panel/30 dark:bg-panel/40",
          "supports-[not(backdrop-filter:blur(1px))]:bg-panel/95",
          "transition-transform duration-300 ease-out",
          hide ? "translate-y-full" : "translate-y-0",
        )}
        style={{
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          backdropFilter: "blur(30px) saturate(200%)",
          WebkitBackdropFilter: "blur(30px) saturate(200%)",
          // Borda top hairline com gradient âmbar muito sutil — "linha de luz"
          borderTop: "1px solid rgba(var(--brand-amber-rgb), 0.25)",
          // Composição:
          // 1) inset top: luz branca incidindo no vidro
          // 2) inset top âmbar: tint da marca quente
          // 3) drop shadow: profundidade neutra
          boxShadow: [
            "inset 0 1px 0 rgba(255, 255, 255, 0.25)",
            "inset 0 8px 24px -12px rgba(var(--brand-amber-rgb), 0.28)",
            "0 -12px 32px -8px rgba(36, 30, 20, 0.22)",
          ].join(", "),
        }}
      >
        {/* Gradient overlay âmbar suave no topo — luminação da marca */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-[rgba(var(--brand-amber-rgb),0.10)] to-transparent"
        />
        <div className="mx-auto flex h-14 max-w-md items-stretch">
          {PRIMARY_ITEMS.map((item) => {
            const Icon = item.icon
            return (
              <DockButton
                key={item.id}
                active={isActiveHref(pathname, item.href)}
                href={item.href}
                icon={<Icon className="h-[18px] w-[18px]" strokeWidth={2.1} />}
                label={item.label}
              />
            )
          })}
          <DockButton
            active={moreActive || moreOpen}
            onClick={() => setMoreOpen((v) => !v)}
            icon={<MoreHorizontal className="h-[18px] w-[18px]" strokeWidth={2.1} />}
            label="Mais"
          />
        </div>
      </nav>

      <MoreSheet
        isOpen={moreOpen}
        onClose={() => setMoreOpen(false)}
        items={visibleMore}
        pathname={pathname}
        tenantName={tenant?.name || "Menu"}
        onLogout={() => {
          logout()
          router.push("/auth/login")
        }}
      />
    </>
  )
}
